import type { ReactNode } from "react";
import { cx } from "./cx";

export type TagTone = "neutral" | "strong" | "accent" | "quiet" | "waiting" | "attention";

const TAG_TONES: Record<TagTone, string> = {
  neutral: "border-divider text-muted bg-bg",
  strong: "border-border text-fg bg-bg",
  accent: "border-link/45 text-link bg-accent-soft",
  quiet: "border-transparent bg-surface-3 text-muted",
  waiting: "border-waiting/55 text-waiting bg-waiting-soft",
  attention: "border-attention/45 text-attention bg-attention-soft",
};

export function Tag({
  tone = "neutral",
  children,
  className,
}: {
  tone?: TagTone;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cx(
        "inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-[7px] text-xs leading-[17px] font-medium",
        TAG_TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

export type StatusTone = "ok" | "invited" | "off" | "waiting" | "attention" | "running";

const DOT: Record<StatusTone, string> = {
  ok: "bg-done",
  running: "bg-link",
  invited: "border-[1.5px] border-link bg-transparent",
  off: "border-[1.5px] border-muted bg-transparent",
  waiting: "bg-waiting rounded-[2px]",
  attention: "bg-attention rounded-[2px]",
};

const STATUS_TEXT: Record<StatusTone, string> = {
  ok: "text-fg",
  running: "text-fg",
  invited: "text-fg",
  off: "text-muted",
  waiting: "font-strong text-waiting",
  attention: "text-fg",
};

export function StatusDot({ tone }: { tone: StatusTone }) {
  return <span aria-hidden="true" className={cx("inline-block size-2 flex-none rounded-full", DOT[tone])} />;
}

export function Status({ tone, children }: { tone: StatusTone; children: ReactNode }) {
  return (
    <span className={cx("inline-flex items-center gap-[7px] whitespace-nowrap", STATUS_TEXT[tone])}>
      <StatusDot tone={tone} />
      {children}
    </span>
  );
}

export function initials(name: string): string {
  return name
    .split(/[\s.@_-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0))
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

export function Avatar({
  name,
  size = "sm",
  muted = false,
}: {
  name: string;
  size?: "sm" | "md" | "lg";
  muted?: boolean;
}) {
  return (
    <span
      aria-hidden="true"
      className={cx(
        "inline-grid flex-none place-items-center rounded-full font-mono font-semibold tracking-[0.02em]",
        size === "sm" && "size-[22px] text-[9px]",
        size === "md" && "size-[26px] text-2xs",
        size === "lg" && "size-10 text-base",
        muted ? "bg-surface-3 text-muted" : "bg-accent-soft text-link",
      )}
    >
      {initials(name)}
    </span>
  );
}
